import { Fragment, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pickPupTarget, pickPupDiscard, pickPupCardTarget, pickContractSeat, multiDiscardPupCards } from '../../redux/pupApi';
import { queueFreeze } from '../../redux/freezeApi';
import { freezeNoticeShown } from '../../redux/gameSlice';
import { markFreezeHintClicked, shouldFlashFreezeHint, FREEZE_HINT_FLASH_MS } from './freezeHint';
import Player from './Player';
import StagePhaseIndicator from './StagePhaseIndicator';
import FreezeCenterNotice from './FreezeCenterNotice';
import CommunityCards from './CommunityCards';
import HoleCards from './HoleCards';
import SeatCards from './SeatCards';
import DealerDraw from './DealerDraw';
import ProfilePicture from './ProfilePicture';
import ActionPanel from './ActionPanel';
import PreActionPanel from './PreActionPanel';
import ActionBubble from './ActionBubble';
import { usePreActionAutoFire } from './usePreActionAutoFire';
import ShowCardsButton from './ShowCardsButton';
import ChatWindow from './ChatWindow';
import ActionLog from './ActionLog';
import PupHand from '../pupCards/PupHand';
import PupStagingBox from '../pupCards/PupStagingBox';
import PupAutoPlayBox from '../pupCards/PupAutoPlayBox';
import { buildPupConditionContext } from '../pupCards/pupConditions';
import PupPlayOverlay from '../pupCards/PupPlayOverlay';
import PupCard from '../pupCards/PupCard';
import PupNoticeStrip from '../pupCards/PupNoticeStrip';
import SniperGuessPanel from '../pupCards/SniperGuessPanel';
import TestPupPanel from '../TESTINGpup_DltInProd/TestPupPanel'; // TEST-ONLY: inert unless TESTING_ENABLED
import SitOutToggle from './SitOutToggle';
import SittingOutBox from './SittingOutBox';
import ChipStack from './ChipStack';
import { useChipAnimations, ChipFlight } from './useChipAnimations';
import { useTableGeometry, TableGeometryContext } from './useTableGeometry';
import { hashStr, getChipScale, minRaiseTo, formatAmount } from '../../utilities/chips';
import { avatarSrc } from '../../utilities/avatars';
import './gameroom.css';

// Stable empty reference so the pupPlays selector doesn't churn re-renders when a room has no
// pending play (a fresh `|| []` would be a new array identity every render).
const EMPTY_PUP_STACK = [];

// Component for a single game room display
const GameTable = ({ gameId, game, onSitClick, onLeaveTable, onStandUp, onAddChips, onAddGame, onReplay, replayOpen, pendingSeat, pendingUsername, isSeated, myCards, handInfo, mySeat, leaderUsername }) => {
  // Live table geometry: seats/chips/dealer anchored to the felt rings from the
  // measured table size (see useTableGeometry). Refs feed it the three ring elements.
  // heroSeat/seatCount drive the purely visual hero-at-the-bottom rotation: this viewer's
  // own seat is drawn in the bottom-center slot and the whole ring is spun with it, so
  // relative seat order (and therefore betting order) is preserved. Each table in
  // split-screen rotates independently around its own seat.
  const containerRef = useRef(null);
  const railRef = useRef(null);
  const feltRef = useRef(null);
  const geometry = useTableGeometry({
    containerRef, railRef, feltRef,
    heroSeat: mySeat ?? null,
    seatCount: game?.maxPlayers || 6,
  });

  // Chip display state: bet stacks, lagged pot value, and in-flight chip animations
  const { displayedBets, displayedPot, flights } = useChipAnimations(gameId, game, geometry);

  // Client-side pre-action: auto-submit a queued action the instant it becomes the hero's turn.
  usePreActionAutoFire(gameId, game, mySeat);

  // BASELINE size of the top-left table-info box: the size that box has carrying only the rows
  // EVERY table always shows (name / Players / Blinds), measured off the hidden `.game-info-baseline`
  // twin rendered beside it below. The stage/phase indicator sizes its boxes off this (height =
  // 30% of it, width = it), so the situational rows the real box grows and drops mid-hand — the
  // pot, the stage line, a bomb-pot banner, an SnG/tournament level — can never resize the top
  // bar. It stays exactly the size it is the moment a player sits down.
  // A callback ref wires a ResizeObserver the moment the twin mounts — robust to the `!game` early
  // return below, and re-measuring if the baseline text itself ever changes (a Sit-and-Go's blind
  // level rising is the only one that does, and the real box grows with it).
  const infoObserverRef = useRef(null);
  const [infoBaseSize, setInfoBaseSize] = useState(null);
  const infoBaselineRef = useCallback((node) => {
    if (infoObserverRef.current) { infoObserverRef.current.disconnect(); infoObserverRef.current = null; }
    if (!node) return;
    const measure = () => {
      const r = node.getBoundingClientRect();
      if (!r.width || !r.height) return;
      // Keep the object identity stable when nothing actually moved, so a sub-pixel reflow
      // can't churn a re-render of the whole table.
      setInfoBaseSize((prev) => (
        prev && Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5
          ? prev
          : { width: r.width, height: r.height }
      ));
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      infoObserverRef.current = ro;
    }
  }, []);

  const dispatch = useDispatch();
  // The Power-Up play STACK at this table (card(s) on the felt — the 2s reveal window of an
  // untargeted card, the targeting/line windows of a targeted one, and any INTERRUPT cards
  // stacked on top). Usually one entry; an interrupt pushes another on top, resolving LIFO.
  const pupStack = useSelector((state) => state.pup.plays[gameId]) || EMPTY_PUP_STACK;
  // The TOP of the stack = the ACTIVE play. It drives every interaction (targeting clicks,
  // discard/card pickers, opt-in) AND every "table is frozen" guard (`!!pupPlay` ⟺ a card is on
  // the felt). Deeper entries render dimmed but are never interactive.
  const pupPlay = pupStack.length ? pupStack[pupStack.length - 1] : null;
  // Round-scoped Power-Up reveal displays (all cleared on the next GAME_DEAL): hole cards
  // force-revealed to the whole table (Go Fish), opponent cards revealed privately to THIS
  // player (Peek/Peeper), and the community token(s) a wild landed on.
  const pupPublicReveals = useSelector((state) => state.pup.publicReveals[gameId]);
  const pupPeeks = useSelector((state) => state.pup.peeks[gameId]);
  // THIS player's own hole cards an opponent secretly saw (Peek/Peeper): { token: [byUsername] }.
  // Drives the owner-only eye badge on the hero's hand (HoleCards).
  const pupMyPeeked = useSelector((state) => state.pup.myPeeked[gameId]);
  // Cards a value-modifying Power-Up rewrote in place this round (Plus/Minus One, Suit Change,
  // Set Ace, One Man's Trash, Magic Trick): { <cardId>: [mods] }. Drives the "modified" badge +
  // hold-to-see-original on the hero's own hand (private, owner-only) and the board (public).
  // Keyed by card IDENTITY — a card's history follows the card, not the slot it sits in.
  const pupCardMods = useSelector((state) => state.pup.cardMods[gameId]);
  // Which cards are WILD, as [{zone, seat, index}] from the authoritative game payload. Folded
  // into position Sets below; wildness is never matched by token (two cards can share one).
  const pupWildRefs = useSelector((state) => state.pup.wildRefs[gameId]);
  // The hero's own hole-card identities (private), positionally aligned with myCards.
  const myHoleCardIds = useSelector((state) => state.pup.myHoleCardIds[gameId]);
  const boardWildIndexes = useMemo(() => new Set(
    (pupWildRefs || []).filter((r) => r?.zone === 'COMMUNITY').map((r) => r.index)
  ), [pupWildRefs]);
  const myWildIndexes = useMemo(() => new Set(
    (pupWildRefs || []).filter((r) => r?.zone === 'HOLE' && r.seat === mySeat).map((r) => r.index)
  ), [pupWildRefs, mySeat]);
  const boardCardIds = useMemo(
    () => (game?.communityCardIds ? String(game.communityCardIds).trim().split(/\s+/) : []),
    [game?.communityCardIds]
  );
  // The hero spent their Stream Sniper guess this round (set by sniperGuess.fulfilled,
  // swept on the next GAME_DEAL) — hides the guess button until the next deal.
  const sniperGuessSpent = useSelector((state) => !!state.pup.sniperGuessUsed[gameId]);
  // The hero's private deck peek (Premonition/Button Up/Peaceful wanderer) — a small
  // "top of deck" strip until the next deal reshuffles under it.
  const pupDeckPeek = useSelector((state) => state.pup.deckPeeks[gameId]);
  // The LIVE view of that peek: the server stamped the deck's size at the peek (deckSize),
  // so every card the table has since consumed off the top (a drawn extra hole card, the
  // burn before a street) shows up as game.deckRemaining falling below it — drop that many
  // peeked cards from the front, and the strip disappears once none of what the player saw
  // is still on top. A legacy peek without deckSize renders as-is until the deal sweep.
  const deckPeekCards = useMemo(() => {
    if (!pupDeckPeek || !pupDeckPeek.cards || !pupDeckPeek.cards.length) return [];
    const { cards, deckSize } = pupDeckPeek;
    const live = game?.deckRemaining;
    if (typeof deckSize !== 'number' || typeof live !== 'number') return cards;
    const consumed = Math.max(0, deckSize - live);
    return consumed > 0 ? cards.slice(consumed) : cards;
  }, [pupDeckPeek, game?.deckRemaining]);
  // The strip's top card is SET TO BE BURNED when a street deal is still coming (the burn-
  // and-turn ritual precedes the flop/turn/river deals — never showdown, and on the river
  // no deal remains). Gated on the burn actually having a card to spare (the engine skips
  // it rather than starve the deal: flop needs 3 cards after the burn, turn/river 1).
  const deckPeekBurnNext =
    (game?.currentStage === 'preflop' && game?.deckRemaining > 3)
    || ((game?.currentStage === 'flop' || game?.currentStage === 'turn')
        && game?.deckRemaining > 1);
  // Good economy's discounted exchange rate — private, and the one conditional-card precondition
  // the public payload can't carry.
  const exchangeRateTwo = useSelector((state) => !!state.pup.exchangeRateTwo[gameId]);
  // Super Suit: the suit the hero armed on the overlay island before clicking a hole card.
  // Reset whenever the play on the felt changes (a new play = a fresh choice).
  const [superSuit, setSuperSuit] = useState(null);
  const pupPlayId = useSelector((state) => {
    const stack = state.pup.plays[gameId];
    return stack && stack.length ? stack[stack.length - 1].playId : null;
  });
  useEffect(() => { setSuperSuit(null); }, [pupPlayId]);

  // This player's PRIVATE queued-freeze selection ({ point, used }) — drives the dark-blue
  // selected dot on the stage/phase indicator (only the owner ever sees their selection).
  const queuedFreezeState = useSelector((state) => state.games.queuedFreeze[gameId]);

  // First-time freeze-hint flash: while this ACCOUNT has never clicked a queued-freeze dot
  // (account-level tri-state latch in gameSlice, hydrated from snapshot/mycards; plus the
  // localStorage belt inside shouldFlashFreezeHint), the indicator's dots blink yellow for
  // 2s each time the table ENTERS the Turn stage — but only once the hero has been seated
  // here for 5 minutes. The seat clock is deliberately in-memory: a refresh/reopen restarts
  // it, which only ever DELAYS the hint (it must never fire when it shouldn't).
  const freezeHintServerClicked = useSelector((state) => state.games.freezeQueueClicked);
  const [freezeHintFlash, setFreezeHintFlash] = useState(false);
  const seatedAtRef = useRef(null);
  const prevStageRef = useRef(null);
  const flashTimerRef = useRef(null);
  useEffect(() => {
    if (mySeat != null) {
      if (seatedAtRef.current == null) seatedAtRef.current = Date.now();
    } else {
      seatedAtRef.current = null;
    }
  }, [mySeat]);
  const stageNow = game?.currentStage;
  useEffect(() => {
    const prevStage = prevStageRef.current;
    prevStageRef.current = stageNow;
    // Fire exactly once per Turn-stage ENTRY (the extra deps only re-run this while the
    // stage is unchanged, which the prevStage guard swallows).
    if (stageNow !== 'turn' || prevStage === 'turn') return;
    if (!shouldFlashFreezeHint({
      serverClicked: freezeHintServerClicked, mySeat,
      seatedAtMs: seatedAtRef.current, now: Date.now(),
    })) return;
    setFreezeHintFlash(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFreezeHintFlash(false), FREEZE_HINT_FLASH_MS);
  }, [stageNow, freezeHintServerClicked, mySeat]);
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  // The Sit Out checkbox is REMOVED the moment the hero is sitting out / away, and only comes
  // back once they are active again AND a NEW hand has dealt them in (handId advanced past the
  // hand the removal happened at) — never in between. Rejoining is exclusively the away box's
  // "Rejoin Table" button (the server refuses sittingOut=false too), so the checkbox can never
  // double as a rejoin path or race one. Computed here, above the !game early return, because
  // it is a hook; heroOut deliberately mirrors seatIsAway below plus the cash isSittingOut flag.
  const heroEntry = mySeat != null ? game?.players?.[mySeat] : null;
  const heroOut = !!(heroEntry?.isSittingOut || heroEntry?.isAway === true
    || (game?.awaySeats || []).includes(mySeat)
    || (mySeat != null && game?.awayDeadlines?.[mySeat] != null));
  const heroDealtInLiveHand = ['preflop', 'flop', 'turn', 'river'].includes(game?.currentStage)
    && (heroEntry?.numHoleCards || 0) > 0;
  const liveHandId = game?.handId || 0;
  // The handId the removal happened at; null = the checkbox is not removed.
  const [sitOutRemovedAtHand, setSitOutRemovedAtHand] = useState(null);
  useEffect(() => {
    setSitOutRemovedAtHand((prev) => {
      if (heroOut) return prev ?? liveHandId;
      if (prev != null && heroDealtInLiveHand && liveHandId > prev) return null;
      return prev;
    });
  }, [heroOut, heroDealtInLiveHand, liveHandId]);

  if (!game) return null;

  const chipScale = getChipScale(game.smallBlind, game.bigBlind);
  const seatNumbers = Array.from({ length: game.maxPlayers || 6 }, (_, i) => i + 1);
  // A round is live (someone is to act) — gates the per-seat time-bank bar.
  const roundActive = ['preflop', 'flop', 'turn', 'river'].includes(game.currentStage);
  // "Replay last hand": enabled once a completed hand exists. handId >= 2 guarantees a prior hand
  // finished (a deal only happens after the previous hand resolved); handId >= 1 while the table is
  // idle between hands means the just-finished hand is the previous one (?hand=previous returns it).
  const hasCompletedHand = game.handId >= 2
    || (game.handId >= 1 && (game.currentStage === 'start' || game.currentStage === 'waiting' || game.currentStage == null));
  const replayDisabled = !!replayOpen || !hasCompletedHand;
  const replayTooltip = replayOpen
    ? 'A replay for this table is already open'
    : (!hasCompletedHand ? 'No completed hand to replay yet' : 'Replay the last completed hand');
  // Player-controlled FREEZE TIME (distinct from a resolving Power-Up's table freeze):
  // table-level frozenSeats rides every game payload ([] when none). While any seat has time
  // frozen, ONLY the freezer(s) may act or play a Power-Up — mirror the server by gating the
  // action panels, the on-seat turn clock and the pup dock below. The freezer keeps their own
  // panel/hand live.
  const frozenSeats = game.frozenSeats || [];
  const timeFrozen = frozenSeats.length > 0;
  const heroFrozen = mySeat != null && frozenSeats.includes(mySeat);
  // Queue (or toggle off) the one-per-round 10s freeze at a stage/phase indicator dot. The
  // selection is private (no broadcast); re-selecting after this round's queued freeze
  // already fired answers 400 with EXACTLY "already executed a queued freeze" — surfaced as
  // the centered 4s notice (FreezeCenterNotice).
  const onQueueSelect = (stageKey, phaseKey) => {
    // Any dot click permanently retires the first-time hint: localStorage belt now (covers
    // a refresh racing the server write), redux latch via queueFreeze.pending, server
    // profile flag via the endpoint — and the current flash stops on the spot.
    markFreezeHintClicked();
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFreezeHintFlash(false);
    dispatch(queueFreeze({ roomId: gameId, stage: stageKey, phase: phaseKey ?? null }))
      .unwrap()
      .catch((msg) => {
        if (typeof msg === 'string' && msg.includes('already executed a queued freeze')) {
          dispatch(freezeNoticeShown({ roomId: gameId, text: 'already executed a queued freeze' }));
        }
      });
  };
  // Flying Blind (per-seat `blind` flags ride every game payload): the hero playing blind
  // renders face-down backs at their own seat and loses the hole-card-picking Power-Ups;
  // while any blind seat is still contesting, every NON-blind action (bet/call/raise) pays
  // the 2-big-blind surcharge into the blind side pot — surfaced as a hint on the panel.
  const heroBlind = mySeat != null && !!game.players?.[mySeat]?.blind;
  // Everything the CONDITIONAL Power-Ups' rings need to answer "is this card's precondition met
  // right now?" — the hero's hand, the board, and the three public scalars the server ships for
  // exactly this (deckRemaining / stageBoardStart / runItTwice). Feeds both the dock (which
  // suppresses the blue ring on these cards in favour of a red one) and the auto-play box.
  const pupConditionContext = buildPupConditionContext({
    game, myCards, mySeat, exchangeRateTwo,
  });
  const blindContesting = Object.values(game.players || {})
    .some((p) => p && p.blind && !p.isFolded);
  const blindSurcharge = roundActive && blindContesting && !heroBlind
    ? 2 * (game.bigBlind || 0) : 0;
  // The hero may still JOIN a pending opt-in play (Flying Blind, mode 'optIn'): seated, in
  // the round (unfolded ≙ not sitting out at predeal), not the player, not already opted.
  // The backend re-validates the join.
  const heroSeatPlayer = mySeat != null ? game.players?.[mySeat] : null;
  const canOptIn = !!(pupPlay && pupPlay.mode === 'optIn'
    && mySeat != null && pupPlay.seat !== mySeat
    && heroSeatPlayer && !heroSeatPlayer.isPending && !heroSeatPlayer.isEmpty
    && !heroSeatPlayer.isFolded && !heroSeatPlayer.isSittingOut
    && !(pupPlay.optedSeats || []).includes(mySeat));
  // Display names for the seats that already joined the opt-in (shown under the card).
  const optedNames = pupPlay && pupPlay.mode === 'optIn'
    ? (pupPlay.optedSeats || []).map((s) => game.players?.[s]?.username || `Seat ${s}`)
    : [];
  const seatName = (s) => game.players?.[s]?.username || `Seat ${s}`;
  // The hero may still ANSWER a pending Negotiation vote (mode 'voting'): unfolded, dealt in
  // (numHoleCards > 0 on the public snapshot), not the player (who auto-voted yes), not yet
  // in either tally. The backend re-validates the vote.
  const canVote = !!(pupPlay && pupPlay.mode === 'voting'
    && mySeat != null && pupPlay.seat !== mySeat
    && heroSeatPlayer && !heroSeatPlayer.isPending && !heroSeatPlayer.isEmpty
    && !heroSeatPlayer.isFolded && (heroSeatPlayer.numHoleCards || 0) > 0
    && !(pupPlay.yesSeats || []).includes(mySeat)
    && !(pupPlay.noSeats || []).includes(mySeat));
  const yesNames = pupPlay && pupPlay.mode === 'voting'
    ? (pupPlay.yesSeats || []).map(seatName) : [];
  const noNames = pupPlay && pupPlay.mode === 'voting'
    ? (pupPlay.noSeats || []).map(seatName) : [];
  // Binding Contract picking chain (mode 'seatPicking'): whose window is open + the public
  // picks made so far, resolved to display names for the overlay.
  const currentPickerName = pupPlay && pupPlay.mode === 'seatPicking'
    && pupPlay.currentPickerSeat != null
    ? seatName(pupPlay.currentPickerSeat) : null;
  const picksSummary = pupPlay && pupPlay.mode === 'seatPicking' && pupPlay.picks
    ? Object.entries(pupPlay.picks).map(([from, to]) => `${seatName(Number(from))} → ${seatName(to)}`)
    : [];
  // Stream Sniper: the hero owns the permanent, the round is live, exactly ONE other
  // unfolded dealt-in player remains, no Power-Up is resolving, and this round's guess is
  // unspent — mount the guess button/panel. The backend re-validates everything.
  const heroHasSniper = !!(heroSeatPlayer
    && (heroSeatPlayer.permanentPupCards || []).includes('STREAM_SNIPER'));
  const sniperOthers = heroHasSniper
    ? Object.entries(game.players || {}).filter(([s, p]) => Number(s) !== mySeat
        && p && !p.isEmpty && !p.isPending && !p.isFolded && (p.numHoleCards || 0) > 0)
    : [];
  const sniperEligible = !!(heroHasSniper && roundActive && !sniperGuessSpent && !pupPlay
    && mySeat != null && heroSeatPlayer && !heroSeatPlayer.isFolded
    && myCards && myCards.length > 0
    && sniperOthers.length === 1);
  const sniperTargetSeat = sniperEligible ? Number(sniperOthers[0][0]) : null;
  const sniperTargetCount = sniperEligible
    ? (sniperOthers[0][1].numHoleCards || game.holeCardCount || 2) : 0;
  // The guess UI is anchored to the Stream Sniper permanent it belongs to, not docked in a
  // screen corner: the SAME permanentStyle anchor the felt card uses, resolved from the
  // card's index inside the hero's own permanentPupCards (so a hero holding several
  // permanents still gets the Sniper's slot). Undefined until the geometry has a bet
  // position for the seat, in which case the panel falls back to its CSS corner dock.
  const sniperAnchorStyle = sniperEligible
    ? geometry.permanentStyle(mySeat,
      (heroSeatPlayer.permanentPupCards || []).indexOf('STREAM_SNIPER'))
    : null;
  // Chosen-suit cardTargeting plays (Super Suit; Ace Up my Sleeve rides the same channel —
  // the forged Ace's suit is the player's pick): the overlay grows the 4-suit island; the
  // hole-card click then carries the armed suit. Only the hero's own play qualifies.
  const superSuitActive = !!(pupPlay && pupPlay.mode === 'cardTargeting'
    && mySeat != null && pupPlay.seat === mySeat
    && ['SUPER_SUIT', 'ACE_UP_MY_SLEEVE'].includes(pupPlay.card));
  // Magic Trick's combined-pick window: the hero assembles three actions on the overlay
  // island; the hole-card indexes for shift/suit come from the hero's own hand size.
  const trickActive = !!(pupPlay && pupPlay.mode === 'tricking'
    && mySeat != null && pupPlay.seat === mySeat);
  // Grand exchange's multi-discard window: the hero's own hole cards become a multi-select
  // with a confirm button (HoleCards owns the selection).
  const multiDiscardActive = !!(pupPlay && pupPlay.mode === 'multiDiscarding'
    && mySeat != null && pupPlay.seat === mySeat);
  // A LIVE tournament table: the chips here are table-instance currency, so there is no
  // removal clock at all and "leave" only parks the seat away (still dealt in, still posting
  // blinds) until the player rejoins or busts.
  const tournamentChips = !!game.tournamentChips;
  // Is a seat unattended (the engine acts for it)? The persisted server flag first, the
  // in-memory away set second, and the legacy cash removal clock third — a cash seat with a
  // running countdown is away by definition.
  const seatIsAway = (seat) => game.players?.[seat]?.isAway === true
    || (game.awaySeats || []).includes(seat)
    || game.awayDeadlines?.[seat] != null;
  // The current user is parked: shown once the player is out of the round (a voluntary cash
  // Sit Out defers this until they fold; a time-bank expiry, a leave or a disconnect starts it
  // immediately). Drives the centered away box and suppresses the action panel.
  const heroParked = mySeat != null && seatIsAway(mySeat);
  // Seats that won the round, used to gold-border the winning hand's cards during the
  // showdown window (mirrors the dealer-draw winner highlight). winners carries one
  // entry per winning seat across all pots; only relevant while showing the showdown.
  const winningSeats = game.currentStage === 'showdown'
    ? new Set((game.winners || []).map((w) => w.seat))
    : new Set();
  // Gold-border ONLY the five cards that make each winner's best hand — the hole cards used
  // PLUS the community cards used — not the winner's whole hand. The backend sends those five
  // per revealed seat as showdownReveals[seat].bestHandRefs: POSITIONS, not tokens. Two cards
  // can legitimately hold the same token (Suit Change / Super Suit rewrite one card into
  // another's value), so matching by token lit whichever card matched first — often the wrong one.
  const bestHandRefsForSeat = (seat) => {
    const refs = game.showdownReveals?.[seat]?.bestHandRefs;
    return Array.isArray(refs) && refs.length ? refs : null;
  };
  // Board positions to highlight = the union across every winning seat (a community card used by
  // any winner lights up). Empty except during the showdown window.
  const winningBoardIndexes = new Set();
  if (game.currentStage === 'showdown') {
    winningSeats.forEach((seat) => {
      (bestHandRefsForSeat(seat) || [])
        .filter((r) => r?.zone === 'COMMUNITY')
        .forEach((r) => winningBoardIndexes.add(r.index));
    });
  }
  // A seat's own hole positions within its best five.
  const heroHoleHighlight = (seat, heldCount) => {
    if (seat == null || !winningSeats.has(seat)) return null;
    const refs = bestHandRefsForSeat(seat);
    if (refs) {
      return new Set(refs.filter((r) => r?.zone === 'HOLE').map((r) => r.index));
    }
    // No evaluated hand (a last-one-standing win) — highlight the whole hand so a win still reads.
    return new Set(Array.from({ length: heldCount || 0 }, (_, i) => i));
  };
  const heroHighlight = heroHoleHighlight(mySeat, myCards ? myCards.length : 0);
  // Chips each winning seat collected this round, summed across pots. Drives the
  // pop-and-float "+N" indicator beside each winner during the showdown window.
  const winAmountBySeat = {};
  if (game.currentStage === 'showdown') {
    (game.winners || []).forEach((w) => {
      if (w && w.seat != null) winAmountBySeat[w.seat] = (winAmountBySeat[w.seat] || 0) + (w.amount || 0);
    });
  }

  // The table-info rows EVERY table always shows, in every stage — its baseline content. Rendered
  // twice: in the real info box (which then adds the situational rows below it) and in the hidden
  // measurement twin the stage/phase indicator is sized against. One element, so the twin can
  // never drift from the box it stands in for.
  const infoBaseRows = (
    <>
      <h3>{game.name}</h3>
      <p>Players: {game.numPlayers}/{game.maxPlayers}</p>
      <p>Blinds: {game.smallBlind}/{game.bigBlind}</p>
    </>
  );

  return (
    <TableGeometryContext.Provider value={geometry}>
    <div className="game-table" ref={containerRef}>
      {/* CSS-drawn table (floor + raised rail + felt). Replaces blue-table.png. */}
      <div className="table-background">
        <div className="poker-table" ref={railRef}>
          <div className="table-felt" ref={feltRef} />
        </div>
      </div>

      {/* Top-center stage/phase indicator (sized off the table-info box's BASELINE content, so it
          never resizes mid-hand — see infoBaselineRef above). Also hosts the phase countdown bar,
          docked flush under the boxes and spanning their full width, plus the freeze-time islands:
          the dots double as queued-freeze picks for seated players and the circular
          Freeze/Unfreeze button docks right of the row. */}
      <StagePhaseIndicator
        currentStage={game.currentStage}
        currentPhase={game.currentPhase}
        infoBaseSize={infoBaseSize}
        // Ceiling measured off the live table: how much room there is above the TOP-CENTER
        // seat's Power-Up tier pips. The panel clamps its box height to this so it can never
        // cover a player's counts (the pips are z 16 and would just paint over it anyway).
        maxBoxHeight={geometry.topPipCeilingPx}
        pupActive={!!pupPlay}
        phaseDurationMs={game.phaseDurationMs}
        phaseStartTime={game.phaseStartTime}
        freezeActive={timeFrozen}
        queueEnabled={mySeat != null}
        queuedSelection={queuedFreezeState?.point || null}
        onQueueSelect={onQueueSelect}
        hintFlash={freezeHintFlash}
        roomId={gameId}
        mySeat={mySeat}
        frozenSeats={frozenSeats}
        freezeTimeBank={game.players?.[mySeat]?.freezeTimeBank ?? 0}
        freezeStartTime={game.freezeStartTime}
        freezeDurationMs={game.freezeDurationMs}
      />

      {/* Centered 4s freeze notice ("already executed a queued freeze"). */}
      <FreezeCenterNotice roomId={gameId} />

      {/* Dealer chip */}
      {game.dealerSeat && geometry.dealerStyle(game.dealerSeat) && (
        <div className="dealer-chip" style={geometry.dealerStyle(game.dealerSeat)}>
          D
        </div>
      )}

      {/* Bounty button token — the gold twin of the dealer chip, on the same anchor. The
          token can now OUTLIVE its holder (a departure leaves it UNASSIGNED, seat null, with
          its 8-round lifetime still ticking), so this renders only when a seat is assigned
          AND still occupied. The occupancy half earns its keep: PLAYER_LEFT ships only
          { players, numPlayers } and the client MERGES payloads, so the token fields stay
          stale until the next full GAME_DEAL/GAME_ACTION and the chip would otherwise hang
          over an empty seat for the length of a result hold. When one seat holds BOTH chips,
          bountyStyle slides the B one chip-width along the ring; the D never moves. */}
      {game.bountyButtonSeat != null
        && game.players?.[game.bountyButtonSeat]
        && !game.players[game.bountyButtonSeat].isEmpty
        && geometry.bountyStyle(game.bountyButtonSeat) && (
        <div
          className="bounty-chip"
          style={geometry.bountyStyle(game.bountyButtonSeat,
            game.bountyButtonSeat === game.dealerSeat)}
          title={`Bounty button — ${game.bountyButtonRounds || 0} round${
            game.bountyButtonRounds === 1 ? '' : 's'} left; winning a round while holding it `
            + 'collects 1 BB from every other player'}
        >
          B
        </div>
      )}

      {/* Opening high-card-for-the-button draw, before the first round is dealt */}
      {game.dealerDraw && <DealerDraw draw={game.dealerDraw} />}

      {/* Community cards centered on table; pot lags the live value during animations.
          pots carries the live main/side-pot split (only present while a player is all-in). */}
      <CommunityCards cards={game.communityCards} cardIds={boardCardIds} pot={displayedPot} pots={game.pots} blindPot={game.blindSidePot || 0} chipScale={chipScale} highlightIndexes={winningBoardIndexes} wildIndexes={boardWildIndexes}
        // A board card a value-modifying Power-Up rewrote (a board-targeted rank/suit shift) →
        // public "modified" badge + hold-to-see-original. { <cardId>: [{by, original, ...}] }.
        mods={pupCardMods}
        // A card-targeting Power-Up whose scope reaches the board (Shrinkify's ANY, or a
        // COMMUNITY / COMMUNITY_OR_OWN_HOLE card) lights the community cards for the hero to pick.
        cardTargetActive={!!(pupPlay && pupPlay.mode === 'cardTargeting'
          && mySeat != null && pupPlay.seat === mySeat
          && (pupPlay.scope === 'ANY' || pupPlay.scope === 'COMMUNITY' || pupPlay.scope === 'COMMUNITY_OR_OWN_HOLE'))}
        onCardTarget={(index) => dispatch(pickPupCardTarget({
          roomId: gameId, zone: 'community', targetSeat: -1, cardIndex: index,
        })).unwrap()}
      />

      {/* (The phase countdown bar now lives inside <StagePhaseIndicator>, docked under the
          stage boxes — see above. Both its sources, the Start/End betting beats and the
          interactive Pre-Deal window, moved there so it anchors to the boxes' bottom edge.) */}

      {/* PERMANENT Power-Up cards parked on the felt (Overflow): each seat's permanentPupCards,
          rendered to the LEFT of the seat in the bet-line↔felt band. Public per-seat snapshot
          data, so they persist across deals until the server drops them (destroy condition). */}
      {Object.entries(game.players || {}).flatMap(([seatStr, pl]) => {
        const seat = Number(seatStr);
        const codes = (pl && pl.permanentPupCards) || [];
        return codes.map((code, i) => {
          const style = geometry.permanentStyle(seat, i);
          if (!style) return null;
          return (
            <div key={`perm-${seat}-${i}-${code}`} className="pup-permanent-card" style={style}>
              <PupCard code={code} roomId={gameId} />
            </div>
          );
        });
      })}

      {/* Power-Up play(s) on this table: each card on the felt (the ACTIVE/top one shimmering
          through its pre-resolve window, with its reveal/targeting/line countdown; deeper
          interrupt-stack entries render dimmed and offset behind it), and (in line mode) the
          glowing card→target line. Pointer-events: none, so target-pick clicks land on the
          seats underneath. */}
      {pupStack.map((p, i) => (
        <PupPlayOverlay
          key={p.playId != null ? p.playId : `idx-${i}`}
          roomId={gameId}
          play={p}
          active={i === pupStack.length - 1}
          stackIndex={pupStack.length - 1 - i}
          seatPositions={geometry.seatPositions}
          mySeat={mySeat}
          canOptIn={canOptIn}
          optedNames={optedNames}
          canVote={canVote}
          yesNames={yesNames}
          noNames={noNames}
          currentPickerName={currentPickerName}
          picksSummary={picksSummary}
          chosenSuit={superSuitActive ? superSuit : null}
          onSuitPick={superSuitActive ? setSuperSuit : null}
          canTrick={trickActive}
          trickHoleCount={trickActive && myCards ? myCards.length : 0}
          freezeActive={timeFrozen}
        />
      ))}

      {/* Transient Power-Up announcements (Sniper guesses, failed votes, countered plays). */}
      <PupNoticeStrip roomId={gameId} />

      {/* Negotiated split: a passed Negotiation vote ended the round — banner through the
          result hold (GAME_SHOWDOWN carries negotiated: true; swept on the next deal). */}
      {game.currentStage === 'showdown' && game.negotiated && (
        <div className="pup-notice-strip negotiated-banner-wrap">
          <div className="pup-notice pup-notice-vote">🤝 Pot split by negotiation</div>
        </div>
      )}

      {/* Binding Contract settlement: per-seat net side-bet results, shown with the showdown. */}
      {game.currentStage === 'showdown' && Array.isArray(game.contractResults)
        && game.contractResults.length > 0 && (
        <div className="contract-results">
          <div className="contract-results-title">Binding Contract</div>
          {game.contractResults.map((r) => (
            <div
              key={`contract-${r.seat}`}
              className={`contract-results-row${(r.net || 0) >= 0 ? ' contract-gain' : ' contract-loss'}`}
            >
              {r.username || `Seat ${r.seat}`}: {(r.net || 0) >= 0 ? '+' : ''}{formatAmount(r.net || 0)}
            </div>
          ))}
        </div>
      )}

      {/* Stream Sniper: the hero's once-per-round heads-up guess (button → rank panel),
          anchored on the hero's own Stream Sniper permanent (see sniperAnchorStyle). */}
      {sniperEligible && (
        <SniperGuessPanel
          roomId={gameId}
          targetName={seatName(sniperTargetSeat)}
          count={sniperTargetCount}
          anchorStyle={sniperAnchorStyle}
        />
      )}

      {/* Run It Twice: the second five-card board, its own compact labeled row during the
          showdown (the "— run 1/2" pot entries carry the per-run winners). */}
      {game.currentStage === 'showdown' && game.secondBoard && (
        <div className="second-board">
          <span className="second-board-label">Run 2</span>
          {game.secondBoard.trim().split(/\s+/).map((token, i) => {
            const rank = token.slice(0, -1);
            const suit = token.slice(-1);
            const suitGlyphs = { h: '♥', d: '♦', c: '♣', s: '♠' };
            const red = suit === 'h' || suit === 'd';
            return (
              <div key={`run2-${i}`} className="card card-face" style={{ color: red ? '#f0245e' : '#191e4e' }}>
                <div className="card-rank">{rank}</div>
                <div className="card-suit">{suitGlyphs[suit] || suit}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* The hero's private deck peek (Premonition's four / Button Up's & the Peaceful
          wanderer's three) — a small "top of deck" strip. Live: cards already consumed off
          the deck's top drop from the front (deckPeekCards), the strip disappears once none
          remain, and the top card wears an orange "Burn" tag while a street deal (and so a
          burn) is still coming. Doubles as the reference for the bury island on the overlay. */}
      {deckPeekCards.length > 0 && (
        <div className="deck-peek-strip">
          <span className="second-board-label">Top of deck</span>
          {deckPeekCards.map((token, i) => {
            const rank = token.slice(0, -1);
            const suit = token.slice(-1);
            const suitGlyphs = { h: '♥', d: '♦', c: '♣', s: '♠' };
            const red = suit === 'h' || suit === 'd';
            return (
              <div key={`peek-${i}`} className="card card-face" style={{ color: red ? '#f0245e' : '#191e4e' }}>
                <div className="card-rank">{rank}</div>
                <div className="card-suit">{suitGlyphs[suit] || suit}</div>
                {i === 0 && deckPeekBurnNext && (
                  <div className="deck-peek-burn">Burn</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* "+N" winnings indicator: pops in then floats up and fades (Instagram-heart
          style), shown just to the left of each winning seat during the showdown window. */}
      {game.currentStage === 'showdown' && seatNumbers.map((seat) => {
        const amt = winAmountBySeat[seat];
        const p = geometry.seatPositions[seat];
        if (!amt || amt <= 0 || !p) return null;
        // Offset scales with the avatar circle so the "+N" clears its left edge.
        const winFloatOffset = Math.round(geometry.avatarRadiusPx + 26);
        return (
          <div
            key={`win-${seat}`}
            className="win-float"
            style={{ left: p.left, bottom: p.bottom, transform: `translateX(calc(-50% - ${winFloatOffset}px)) translateY(50%)` }}
          >
            <span className="win-float-inner">+{formatAmount(amt)}</span>
          </div>
        );
      })}

      {/* Bet chip stacks, in front of each seat toward the table center */}
      {seatNumbers.map((seat) => {
        const bet = displayedBets[seat];
        if (!bet || !geometry.betStyle(seat)) return null;
        return (
          <div
            key={`bet-${seat}`}
            className="bet-chips"
            style={geometry.betStyle(seat)}
          >
            <ChipStack
              amount={bet}
              isAllIn={!!game.players?.[seat]?.isAllIn}
              seed={hashStr(`${gameId}:${seat}:${bet}`)}
              scale={chipScale}
            />
          </div>
        );
      })}

      {/* Chips currently flying (bets → pot, pot → winner) */}
      {flights.map((f) => (
        <ChipFlight key={f.id} from={f.from} to={f.to} denoms={f.denoms} />
      ))}

      {/* Player's hole cards. Kept visible (greyed/transparent) once the hero folds so
          they can still see their hand until the round ends; the grey clears on its own
          at the next deal, when the server resets isFolded before GAME_DEAL. While the
          hero's own discard Power-Up waits on its pick (mode 'discarding'), the cards
          become the picker: each pulses and a click discards it (pickPupDiscard). */}
      {myCards && myCards.length > 0 && (
          <HoleCards
            cards={myCards}
            seat={mySeat}
            folded={mySeat != null && !!game.players?.[mySeat]?.isFolded}
            highlightIndexes={heroHighlight}
            roomId={gameId}
            stage={game.currentStage}
            wonByFold={!!game.wonByFold}
            discardActive={!!(pupPlay && pupPlay.mode === 'discarding' && mySeat != null && pupPlay.seat === mySeat)}
            onDiscard={(card) => dispatch(pickPupDiscard({ roomId: gameId, card })).unwrap()}
            // The hero's own card-TARGETING Power-Up (Plus/Minus One, Suit Change) waits on
            // its pick: hole cards become the picker whenever the play's scope allows the
            // hole zone (everything but a pure COMMUNITY scope). The pick sends the card's
            // 0-based index; the backend enforces the own-seat restriction.
            cardTargetActive={!!(pupPlay && pupPlay.mode === 'cardTargeting'
              && mySeat != null && pupPlay.seat === mySeat && pupPlay.scope !== 'COMMUNITY')}
            // Super Suit: the pick carries the suit armed on the overlay island; the server
            // rejects a missing suit only via the effect's fallback, so require it armed
            // client-side by simply not sending until chosen (the overlay hints "Pick a
            // suit first" — an unarmed click still posts and recolors to hearts, the
            // documented magnitude fallback).
            onCardTarget={(card, index) => dispatch(pickPupCardTarget({
              roomId: gameId, zone: 'hole', targetSeat: mySeat, cardIndex: index,
              ...(superSuitActive && superSuit ? { suit: superSuit } : {}),
            })).unwrap()}
            // Own cards an opponent secretly saw (Peek/Peeper) → owner-only eye + tooltip.
            peekedBy={pupMyPeeked}
            // Own cards a value-modifying Power-Up rewrote → owner-only "modified" badge (who
            // changed it) + hold-to-see-original. { <cardId>: [{by, original, current, code}] }.
            modifiedBy={pupCardMods}
            cardIds={myHoleCardIds}
            // Round wilds at THIS seat, by position (a rank rule like Seven Ate Nine's all-6s
            // resolves to positions server-side, exactly like the board's).
            wildIndexes={myWildIndexes}
            // Grand exchange: multi-select + confirm on the hero's own hand.
            multiDiscardActive={multiDiscardActive}
            onMultiDiscard={(cardsToSwap) => dispatch(multiDiscardPupCards({
              roomId: gameId, cards: cardsToSwap,
            })).unwrap()}
          />
      )}

      {/* Subtle "current hand" hint, bottom-right. Backend re-evaluates it each stage and
          pushes it privately; hidden once the hero folds (they're out of the hand). */}
      {handInfo && myCards && myCards.length > 0 && mySeat != null && !game.players?.[mySeat]?.isFolded && (
          <div className="current-hand-info">{handInfo}</div>
      )}

      {/* Action panel - shown when it's this player's turn and their removal timer
          isn't running (a voluntary sit-out still plays the current round normally).
          Hidden while a Power-Up is on the felt: the table is frozen server-side (actions
          are refused, the turn timer is paused), so no action is offered until it resolves.
          Also hidden while ANOTHER player has time frozen (only the freezer may act —
          heroFrozen keeps the panel, since the freezer's accepted action ends the freeze). */}
      {mySeat != null && game.activePlayerPosition === mySeat && !heroParked && !pupPlay && !(timeFrozen && !heroFrozen) && game.currentStage && game.currentStage !== 'waiting' && game.currentStage !== 'showdown' && (
          <ActionPanel
              roomId={gameId}
              callAmount={Math.max(0, (game.currentBetLevel || 0) - (game.players?.[mySeat]?.currentBet || 0))}
              minRaise={minRaiseTo(game.players, game.currentBetLevel, game.bigBlind)}
              // Bomb-pot pot limit: cap the slider at raise-to = level + pot + call amount
              // (the server's formula — it stays authoritative and rejects "Raise too big").
              maxRaise={(() => {
                const stackMax = (game.players?.[mySeat]?.currentBet || 0)
                  + (game.players?.[mySeat]?.chips || 0);
                if (!game.bombPotPotLimit) return stackMax;
                const callAmt = Math.max(0,
                  (game.currentBetLevel || 0) - (game.players?.[mySeat]?.currentBet || 0));
                return Math.min(stackMax,
                  (game.currentBetLevel || 0) + (game.pot || 0) + callAmt);
              })()}
              bigBlind={game.bigBlind}
              pot={game.pot}
              currentBetLevel={game.currentBetLevel}
              stage={game.currentStage}
              turnStartTime={game.turnStartTime}
              noBetRaise={!!game.players?.[mySeat]?.noBetRaise}
              noFold={!!game.players?.[mySeat]?.noFold}
              ghostCall={!!game.players?.[mySeat]?.ghostCall}
              blindSurcharge={blindSurcharge}
          />
      )}

      {/* The hero's Power-Up ("Pup") hand — a second, persistent hand docked at the bottom
          of the view. Cards render small and grow on hover; each is playable only during
          its own stage (EXTRA_CARD: pre-flop) while the hero is dealt in and unfolded. */}
      {mySeat != null && (
        <PupHand
          roomId={gameId}
          stage={game.currentStage}
          phase={game.currentPhase}
          mySeat={mySeat}
          folded={!!game.players?.[mySeat]?.isFolded}
          hasHoleCards={!!(myCards && myCards.length > 0)}
          pupPlayActive={!!pupPlay}
          pupPlayMode={pupPlay?.mode || null}
          pupPlayCard={pupPlay?.card || null}
          // Swiper's cost window: the hero's own dock IS the picker — cards pulse and a
          // click posts /games/puphanddiscard (the hole-card discard flow's dock twin).
          pupDiscardActive={!!(pupPlay && pupPlay.mode === 'pupDiscarding'
            && mySeat != null && pupPlay.seat === mySeat)}
          heroBlind={heroBlind}
          freezeBlocksPlay={timeFrozen && !heroFrozen}
          conditionContext={pupConditionContext}
        />
      )}

      {/* Exchange staging box — drag silver cards from the dock into here to queue them; 3 staged
          silvers become 1 gold when the Exchange stage fires at the top of the next round. */}
      {mySeat != null && (
        <PupStagingBox roomId={gameId} stage={game.currentStage} />
      )}

      {/* Auto-play box — the mirror on the LEFT. Drag any card in here and the server plays it at
          the next moment it is legally playable (immediately, if it already is). */}
      {mySeat != null && (
        <PupAutoPlayBox roomId={gameId} conditionContext={pupConditionContext} />
      )}

      {/* TEST-ONLY Pup card harness — renders nothing unless TESTING_ENABLED (see the file). */}
      <TestPupPanel roomId={gameId} mySeat={mySeat} />

      {/* Pre-action bar - shown when the hero is still in the hand but it's NOT their turn.
          The pick auto-fires the instant it becomes their turn (usePreActionAutoFire).
          Hidden entirely while time is frozen (even for the freezer — no queued action
          should fire into a stopped table). */}
      {mySeat != null && roundActive && game.activePlayerPosition !== mySeat && !heroParked
        && !timeFrozen
        && !game.players?.[mySeat]?.isFolded
        && !game.players?.[mySeat]?.isSittingOut
        && ((myCards && myCards.length > 0) || heroBlind) && (
          <PreActionPanel roomId={gameId} />
      )}

      {/* Per-seat action bubble (Fold/Check/Call/Raise). Only during live betting stages, so it
          clears at showdown; the server nulls lastAction at each stage boundary too. */}
      {roundActive && seatNumbers.map((seatNumber) => {
        const lastAction = game.players?.[seatNumber]?.lastAction;
        if (!lastAction) return null;
        return <ActionBubble key={`act-${seatNumber}`} seat={seatNumber} action={lastAction} />;
      })}

      {/* Show-cards button - replaces the action bar during showdown / last-one-standing,
          letting the local player voluntarily reveal their hand. Hidden once they've shown. */}
      {mySeat != null && !heroParked && game.currentStage === 'showdown'
        && myCards && myCards.length > 0
        && game.showdownReveals?.[mySeat]?.action !== 'show' && (
          <ShowCardsButton
              roomId={gameId}
              wonByFold={!!game.wonByFold}
              isWinner={winningSeats.has(mySeat)}
          />
      )}

      {/* Away overlay, centered on the table: a cash seat counts down to removal; a live
          tournament seat has no time limit at all, so it shows no clock. */}
      {heroParked && (
          <SittingOutBox
              roomId={gameId}
              deadline={game.awayDeadlines?.[mySeat]}
              noTimeLimit={tournamentChips}
          />
      )}

      {onAddGame && (
        <button className="add-game-btn" onClick={onAddGame}>
          + Game
        </button>
      )}

      {/* On a live tournament table this is "Sit Away": it maps to the away state (still dealt
          in, still posting blinds, auto check-folding), never to is_sitting_out — a tournament
          seat that stopped being dealt would never bust, and busting is the only way out.
          REMOVED while the hero is sitting out/away, and it stays gone until a new hand deals
          them back in — the away box's "Rejoin Table" button is the only rejoin path. */}
      {mySeat != null && isSeated && !heroOut && sitOutRemovedAtHand == null && (
        <SitOutToggle
          roomId={gameId}
          label={tournamentChips ? 'Sit Away' : 'Sit Out'}
          title={tournamentChips
            ? 'You stay in the tournament: still dealt in, still posting blinds, auto check-folding.'
            : undefined}
        />
      )}

      {/* On a live tournament table leaving does not leave — the seat, its chips and its blind
          obligations stay and the player is parked away — so the button says so. */}
      <button
        className="leave-table-btn"
        title={tournamentChips
          ? 'Your seat, chips and blinds stay in the tournament. Reopen the table to rejoin.'
          : undefined}
        onClick={() => onLeaveTable(gameId)}
      >{tournamentChips ? 'Away' : 'Leave'}</button>

      {/* Stand Up: drop the seat but stay as a spectator to pick a new one (seated only).
          Meaningless on a tournament table — there is no seat to give up. */}
      {mySeat != null && !tournamentChips && (
        <button className="stand-up-btn" onClick={() => onStandUp(gameId)}>Stand Up</button>
      )}

      {/* Cash-table top-up: lower-left control in the same 2x2 cluster as the reference. */}
      {mySeat != null && isSeated && !game.isSitAndGo && !game.isTournament && (
        <button className="add-chips-btn" onClick={() => onAddChips(gameId)}>+ Chips</button>
      )}

      {/* Replay last hand: available to seated players and spectators alike. */}
      <button
        className="replay-btn"
        onClick={() => onReplay && onReplay(gameId)}
        disabled={replayDisabled}
        title={replayTooltip}
      >
        Replay
      </button>

      <ChatWindow gameId={gameId} />
      <ActionLog gameId={gameId} />

      {seatNumbers.map((seatNumber) => {
        // Winner indicator shown in the bubble (below chips) during the showdown window:
        // the made hand name once they've revealed, otherwise just "winner".
        let winnerLabel = null;
        if (game.currentStage === 'showdown' && winningSeats.has(seatNumber)) {
          const reveal = game.showdownReveals?.[seatNumber];
          winnerLabel = reveal?.action === 'show' ? (reveal.handName || 'winner') : 'winner';
        }
        // The hero's own pending targeted Power-Up: opposing seats that are dealt in and
        // unfolded become clickable targets (the backend re-validates the pick). REDIRECT
        // picks a new target for the play BENEATH it, so the not-self rule moves to the
        // BASE play's owner — the hero's own seat pulses too.
        const seatPlayer = game.players?.[seatNumber];
        const redirectBaseSeat = pupPlay && pupPlay.card === 'REDIRECT' && pupStack.length > 1
          ? pupStack[pupStack.length - 2]?.seat : null;
        const isPupTargetable = !!(pupPlay
          && pupPlay.mode === 'targeting'
          && mySeat != null && pupPlay.seat === mySeat
          && (pupPlay.card === 'REDIRECT'
            ? seatNumber !== redirectBaseSeat
            : seatNumber !== mySeat)
          && seatPlayer && !seatPlayer.isPending && !seatPlayer.isEmpty
          && !seatPlayer.isFolded
          && (seatPlayer.numHoleCards || 0) > 0);
        // The hero's turn in a Binding Contract picking chain (mode 'seatPicking'): every
        // unfolded dealt-in seat becomes clickable. Pickers are FOLDED players (v4 rules),
        // so the hero's own folded seat never qualifies — the unfolded filter below already
        // excludes it. Same magenta pulse as targeting, different endpoint.
        const isContractPickable = !!(pupPlay
          && pupPlay.mode === 'seatPicking'
          && mySeat != null && pupPlay.currentPickerSeat === mySeat
          && seatPlayer && !seatPlayer.isPending && !seatPlayer.isEmpty
          && !seatPlayer.isFolded
          && (seatPlayer.numHoleCards || 0) > 0);
        // The seat that PLAYED the Power-Up currently on the felt: highlighted (border glow)
        // for the whole freeze, paired with the curved card→player line in PupPlayOverlay.
        // Clears the instant the play resolves (pupPlay goes undefined).
        const isPupPlayer = !!(pupPlay && seatNumber === pupPlay.seat);
        return (
          <Player
            key={seatNumber}
            seatNumber={seatNumber}
            player={game.players?.[seatNumber]}
            onSitClick={onSitClick}
            gameId={gameId}
            isPending={pendingSeat === seatNumber}
            pendingUsername={pendingUsername}
            isSeated={isSeated}
            activePlayerPosition={game.activePlayerPosition}
            // Freeze the on-seat turn clock while a Power-Up is on the felt OR a player has
            // time frozen — the server has paused/cancelled the turn timer; it re-arms at
            // full on resolve/unfreeze (fresh turnStartTime in the following snapshot), so
            // the bar reappears full, not drained.
            isActiveTurn={roundActive && seatNumber === game.activePlayerPosition && !pupPlay && !timeFrozen}
            turnStartTime={game.turnStartTime}
            mySeat={mySeat}
            isAway={seatIsAway(seatNumber)}
            winnerLabel={winnerLabel}
            isPupTargetable={isPupTargetable || isContractPickable}
            isPupPlayer={isPupPlayer}
            // One click prop serves both flows: a contract pick posts /games/puppick, a
            // targeted play posts /games/puptarget (the modes are mutually exclusive).
            onPupTarget={(seat) => dispatch(isContractPickable
              ? pickContractSeat({ roomId: gameId, pickSeat: seat })
              : pickPupTarget({ roomId: gameId, targetSeat: seat }))}
          />
        );
      })}

      {/* Circular profile picture, centered above each seated player's bubble. Dimmed when the
          seat isn't actively participating (folded / sitting out / away). The leaderboard
          leader's avatar wears a gold crown; a freezing player's wears the blue ring +
          stopwatch badge for the whole freeze; the three tier pips ringing its upper edge show
          how many Silver/Gold/Celestial Power-Ups the seat holds (counts only — public). */}
      {seatNumbers.map((seatNumber) => {
        const player = game.players?.[seatNumber];
        if (!player || player.isEmpty || player.isPending) return null;
        const inactive = player.isFolded || player.isSittingOut || seatIsAway(seatNumber);
        const crowned = leaderUsername != null && player.username === leaderUsername;
        return <ProfilePicture key={`pfp-${seatNumber}`} seat={seatNumber} src={avatarSrc(player.avatar)} dimmed={inactive} crowned={crowned} frozen={frozenSeats.includes(seatNumber)} pupCounts={player.pupTierCounts} />;
      })}

      {/* Opponents' cards, to the right of each seat: backs during a round, faces at showdown.
          count = the table's hole-card count (2/4/5/8) so PLO seats show the right number of
          backs — the client never sees opponents' actual cards mid-hand. */}
      {seatNumbers.map((seatNumber) => {
        // The hero's own seat renders through HoleCards — EXCEPT while flying blind during a
        // live round: their cards are masked server-side (myCards is empty), so their own
        // seat shows the same face-down backs an opponent sees. At showdown the mask lifts
        // (the resolution pushes the real cards) and HoleCards takes over again.
        if (seatNumber === mySeat && !(heroBlind && game.currentStage !== 'showdown')) return null;
        const player = game.players?.[seatNumber];
        if (!player || player.isEmpty || player.isPending) return null;
        // Per-seat count first: a played EXTRA_CARD Power-Up grows one seat's hand beyond
        // the table's variant count, and that seat must show the extra face-down back.
        const backs = player.numHoleCards || game.holeCardCount || 2;

        if (game.currentStage === 'showdown') {
          // A 'show' (auto reveal or a voluntary Show Cards click — works for folded players
          // too) flips face-up; if they won, only the hole cards in their best five get the gold
          // border (fallback: whole shown hand when no best-hand info). A 'muck' clears the cards.
          // With no reveal yet: folded players show nothing; in a last-standing win (wonByFold)
          // no auto-reveal is coming, so show nothing until someone clicks Show Cards; in a
          // regular showdown, unrevealed seats stay face-down until their reveal arrives.
          const reveal = game.showdownReveals?.[seatNumber];
          if (reveal?.action === 'show') {
            const shownCount = (reveal.holeCards || '').trim().split(/\s+/).filter(Boolean).length;
            const highlight = heroHoleHighlight(seatNumber, shownCount);
            return <SeatCards key={`cards-${seatNumber}`} seat={seatNumber} cards={reveal.holeCards} highlightIndexes={highlight} />;
          }
          if (reveal?.action === 'muck') return null;
          if (player.isFolded) return null;
          if (game.wonByFold) return null;
          return <SeatCards key={`cards-${seatNumber}`} seat={seatNumber} faceDown count={backs} />;
        }

        // Exclude the pre-deal stages too: no card exists yet, and numHoleCards is 0 there —
        // the `backs` fallback below would paint phantom backs through the (now interactive,
        // up-to-4s) Pre-Deal window.
        const roundInProgress = game.currentStage && game.currentStage !== 'waiting' && game.currentStage !== 'start'
          && game.currentStage !== 'predeal' && game.currentStage !== 'exchange';
        // Keep showing a seat's cards while they're actively contesting the round. Key on the
        // ACTUAL hand, not on any away/removal state: an AWAY seat on a live tournament table is
        // still dealt in and still in the hand, so hiding its backs would misreport the table.
        // A cash seat that was sat out has numHoleCards 0 anyway, so this covers both.
        const inRound = roundInProgress
          && !player.isFolded
          && (player.numHoleCards || 0) > 0;
        if (!inRound) return null;
        // Cards of this seat revealed mid-round by a Power-Up: to the whole table (Go Fish's
        // named rank) and/or privately to this player (Peek/Peeper). They render face-up
        // (centered, the revealed-hand form) while the seat's remaining unknown cards keep
        // their face-down backs (offset right) — both persist until the next deal.
        const revealed = [...new Set([
          ...(pupPublicReveals?.[seatNumber] || []),
          ...(pupPeeks?.[seatNumber] || []),
        ])];
        // A hero's ANY/HOLE-scope card-targeting play (Shrinkify) can pick an opponent's hole
        // card blindly by position — only on a fully face-down opponent (the index maps 1:1 to
        // the real hole index there; a partially-revealed seat is skipped to avoid index skew).
        const oppCardTargetActive = !!(pupPlay && pupPlay.mode === 'cardTargeting'
          && mySeat != null && pupPlay.seat === mySeat && seatNumber !== mySeat
          && (pupPlay.scope === 'ANY' || pupPlay.scope === 'HOLE'));
        if (revealed.length === 0) {
          return <SeatCards key={`cards-${seatNumber}`} seat={seatNumber} faceDown count={backs} dimmed={!!player.isSittingOut}
            cardTargetActive={oppCardTargetActive}
            onCardTarget={(index) => dispatch(pickPupCardTarget({
              roomId: gameId, zone: 'hole', targetSeat: seatNumber, cardIndex: index,
            })).unwrap()}
          />;
        }
        const hiddenBacks = Math.max(0, backs - revealed.length);
        return (
          <Fragment key={`cards-${seatNumber}`}>
            <SeatCards seat={seatNumber} cards={revealed.join(' ')} dimmed={!!player.isSittingOut} />
            {hiddenBacks > 0 && (
              <SeatCards seat={seatNumber} faceDown count={hiddenBacks} dimmed={!!player.isSittingOut} />
            )}
          </Fragment>
        );
      })}

      <div className="game-info">
        {infoBaseRows}
        {/* Sit-and-Go: blind level while running; registration banner while filling
            (hidden during the opening button draw — the table is full by then). */}
        {game.isSitAndGo && game.sngStarted && game.sngLevel != null && (
            <p>SnG Level: {game.sngLevel}</p>
        )}
        {game.isSitAndGo && !game.sngStarted && !game.dealerDraw && (
            <p>Sit-and-Go — starts when full</p>
        )}
        {/* Multi-table Tournament: the shared blind level (same clock/schedule as Sit-and-Go). */}
        {game.isTournament && game.tournamentStarted && game.tournamentLevel != null && (
            <p>Tournament Level: {game.tournamentLevel}</p>
        )}
        {/* Bomb-pot round: everyone anted instead of posting blinds; pre-flop was skipped. */}
        {(game.bombPotAnteBb || 0) > 0 && (
            <p className="bomb-pot-banner">
              💣 BOMB POT — everyone antes {game.bombPotAnteBb}×BB
              {game.bombPotPotLimit ? ' · pot-limit' : ''}
            </p>
        )}
        {displayedPot > 0 && <p>Pot: {displayedPot}</p>}
        {game.currentStage && game.currentStage !== 'waiting' && (
            <p>Stage: {game.currentStage}</p>
        )}
      </div>

      {/* Hidden measurement twin of the info box carrying ONLY the baseline rows (same element
          above, so the two can never drift). It is what the stage/phase indicator is sized
          against — measuring the REAL box would let a situational row appearing mid-hand (the
          pot, the stage line, a bomb-pot banner, an SnG/tournament level) widen or heighten the
          top bar, which is exactly what this exists to stop. It shares .game-info's box metrics
          by CSS rather than by a copied padding/border constant; `visibility: hidden` keeps it
          measurable while painting nothing and leaving the accessibility tree alone. */}
      <div className="game-info-baseline" aria-hidden="true" ref={infoBaselineRef}>
        {infoBaseRows}
      </div>
    </div>
    </TableGeometryContext.Provider>
  );
};

export default GameTable;
